import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_FLOW = ["pending", "reviewing", "quoted", "replied"];
const STATUS_STYLES = {
  pending:   "border-[#C05C42] bg-[#FAF1ED] text-[#C05C42]",
  reviewing: "border-[#D49A44] bg-[#FCF6E9] text-[#8C6B25]",
  quoted:    "border-[#6A8C5D] bg-[#EFF3EC] text-[#3D5C32]",
  replied:   "border-[#5C6359] bg-[#F1EFEC] text-[#2D322B]",
};

export default function AdminPrescriptions() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");

  const reload = () => {
    setBusy(true);
    api.get("/admin/prescription-requests")
      .then((r) => setItems(r.data || []))
      .finally(() => setBusy(false));
  };
  useEffect(() => { reload(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/prescription-requests/${id}`, { status });
      toast.success(`Marked ${status}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (id) => {
    try {
      await api.patch(`/admin/prescription-requests/${id}`, { internal_notes: notesDraft });
      toast.success("Notes saved.");
      setOpenId(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const open = items.find((i) => i.id === openId);

  return (
    <AdminLayout>
      <p className="label-eyebrow mb-2">Admin · Prescriptions</p>
      <h1 className="font-serif text-4xl text-[#2D322B] leading-tight mb-8">Clinical review queue.</h1>

      {busy && items.length === 0 && <p className="text-[#9A8F7E]">Loading…</p>}
      {items.length === 0 && !busy ? (
        <p className="text-[#9A8F7E]" data-testid="rx-queue-empty">No prescription requests yet.</p>
      ) : (
        <div className="space-y-3" data-testid="rx-queue">
          {items.map((r) => (
            <div key={r.id} data-testid={`rx-card-${r.id}`} className="bg-white border border-[#E8DFD0] p-5">
              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div>
                  <p className="font-serif text-lg text-[#2D322B]">{r.member_name || "—"}</p>
                  <p className="text-xs text-[#9A8F7E] mt-1">
                    <a href={`mailto:${r.member_email}`} className="hover:text-[#C05C42]">{r.member_email}</a>
                    {r.member_plan && <span> · {r.member_plan}</span>}
                    <span> · {formatDate(r.created_at)}</span>
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-[2px] px-3 py-1 border ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>
                  {r.status || "pending"}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <Field label="Goals" value={r.goals} />
                <Field label="Symptoms" value={r.symptoms} />
                <Field label="Current meds" value={r.current_supplements} />
                <Field label="Allergies" value={r.allergies} />
                <Field label="Dosage preference" value={r.dosage_preference} />
                <Field label="Notes" value={r.notes} />
              </div>

              {r.internal_notes && (
                <div className="mt-4 p-3 bg-[#F1ECE4] border-l-2 border-[#C05C42] text-sm text-[#2D322B]">
                  <p className="label-eyebrow mb-1">Internal notes</p>
                  <p className="whitespace-pre-wrap">{r.internal_notes}</p>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {STATUS_FLOW.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(r.id, s)}
                    disabled={r.status === s}
                    data-testid={`rx-status-${r.id}-${s}`}
                    className={`text-[10px] uppercase tracking-[2px] px-3 py-1.5 border transition-colors ${
                      r.status === s ? "bg-[#2D322B] text-[#F9F6F0] border-[#2D322B] cursor-default" :
                      "border-[#D1C7B8] text-[#5C6359] hover:border-[#C05C42] hover:text-[#C05C42]"
                    }`}
                  >
                    Mark {s}
                  </button>
                ))}
                <button
                  onClick={() => { setOpenId(r.id); setNotesDraft(r.internal_notes || ""); }}
                  data-testid={`rx-edit-notes-${r.id}`}
                  className="text-[10px] uppercase tracking-[2px] px-3 py-1.5 border border-[#D1C7B8] text-[#5C6359] hover:border-[#C05C42] hover:text-[#C05C42] ml-auto"
                >
                  Edit notes
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-[#2D322B]/40 flex items-center justify-center p-6 z-50" onClick={() => setOpenId(null)}>
          <div className="bg-white border border-[#E8DFD0] p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="rx-notes-modal">
            <p className="label-eyebrow mb-2">Internal notes — {open.member_name}</p>
            <textarea
              rows={6}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              data-testid="rx-notes-input"
              className="w-full bg-[#F9F6F0] border border-[#D1C7B8] focus:border-[#C05C42] p-3 text-sm text-[#2D322B] outline-none"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setOpenId(null)} className="text-[11px] uppercase tracking-[2px] text-[#5C6359] px-3 py-2">Cancel</button>
              <button onClick={() => saveNotes(open.id)} data-testid="rx-notes-save" className="btn-primary text-[11px]">Save notes</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="label-eyebrow mb-1">{label}</p>
      <p className="text-[#2D322B] whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}
